/**
 * CalendarView - Wraps react-big-calendar with project-specific
 * styling and the ScheduledEvent → calendar-event adapter.
 *
 * What this organism owns:
 *   - Adapting our `ScheduledEvent` shape to react-big-calendar's
 *     expected `{ start: Date, end: Date, allDay, title, resource }`
 *     shape on every render.
 *   - Locale wiring via the date-fns localizer (we use date-fns
 *     because it's tree-shakeable; the moment alternative ships ~70KB).
 *   - Click handling — exposes a single `onSelectEvent(event)` callback
 *     so the parent page can show its EventDetailsPopover.
 *
 * What this organism does NOT own:
 *   - Data fetching (parent passes `events`).
 *   - Filter UI (parent renders EventFiltersBar above this).
 *   - Popover rendering (parent renders EventDetailsPopover separately).
 *
 * Library quirk: react-big-calendar requires its own CSS, imported
 * here once so consumers don't have to remember.
 */
import React, { useMemo, useState } from 'react';
import { Calendar, dateFnsLocalizer, View } from 'react-big-calendar';
/* date-fns v4 ships ESM entrypoints — use named imports rather than
 * the legacy default-per-file pattern. */
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
/* Project responsive overrides for the library's desktop-default CSS —
 * stacks the toolbar and makes the agenda scroll on mobile. */
import './CalendarView.responsive.css';
import {
    getEventCategory,
    type ScheduledEvent,
    type ScheduledEventCategory,
} from '../../../services/scheduledEventsService';

/* ============================================
 * LOCALIZER (module-level singleton — date-fns is stateless)
 * ============================================ */

const locales = {
    'en-US': enUS,
};

const localizer = dateFnsLocalizer({
    format,
    parse,
    /* Sunday is the first day of the week in en-US calendars; the
     * library expects `(date) => number` and uses 0=Sunday. */
    startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 0 }),
    getDay,
    locales,
});

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * What we hand react-big-calendar. A chip can represent a single
 * underlying event (`event` is set) or a same-day project group
 * (`groupedEvents` is set). The two are mutually exclusive — the
 * onSelect handler dispatches based on which is present.
 */
interface RbcEvent {
    title: string;
    start: Date;
    end: Date;
    allDay: boolean;
    /** Pre-computed for the chip's color rule. */
    category: ScheduledEventCategory;
    /** Number of underlying events represented — 1 for ungrouped chips. */
    count: number;
    /** Set for ungrouped chips. */
    event?: ScheduledEvent;
    /** Set for grouped project-WO chips. First entry is the chronological lead. */
    groupedEvents?: ScheduledEvent[];
}

/**
 * Public click payload — either a single event or the full list of
 * events the chip represents.
 */
export type CalendarViewSelection =
    | { kind: 'single'; event: ScheduledEvent }
    | { kind: 'group'; events: ScheduledEvent[] };

export interface CalendarViewProps {
    /** Events to render on the calendar grid */
    events: ScheduledEvent[];
    /** Click handler for an event chip */
    onSelectEvent?: (selection: CalendarViewSelection) => void;
    /** Optional view (month/week/day) — defaults to 'month' */
    defaultView?: View;
    /** Optional className for the outer wrapper */
    className?: string;
}

/* ============================================
 * COLOR MAP
 * ============================================
 * One palette per event category. Inspections use blue to mirror the
 * EventTypeBadge convention; standard WOs keep brand orange; project
 * WOs use a deeper burnt-orange — same family as standard WOs (since
 * they're a kind of work order) but darker so a row of construction
 * work doesn't blur into the standard WO chips around it. */
const CATEGORY_COLORS: Record<ScheduledEventCategory, { bg: string; border: string }> = {
    work_order: { bg: 'var(--brand-primary, #F9550E)', border: 'var(--brand-primary, #F9550E)' },
    project_work_order: { bg: '#C2410C', border: '#9A3412' },
    inspection: { bg: '#1D4ED8', border: '#1E40AF' },
};

/* ============================================
 * COMPONENT
 * ============================================ */

export const CalendarView: React.FC<CalendarViewProps> = ({
    events,
    onSelectEvent,
    defaultView = 'month',
    className,
}) => {
    /* Controlled view state. react-big-calendar's toolbar dispatches
     * onView when the user clicks Day/Week/Month; we mirror that into
     * local state so the Calendar honors the click. Required because
     * uncontrolled mode in v1.19 can silently no-op the toolbar in some
     * host configurations. */
    const [view, setView] = useState<View>(defaultView);
    /* Same story for the focused date — drives the prev/next/today
     * buttons in the toolbar. */
    const [date, setDate] = useState<Date>(new Date());

    /* Adapt every render. Two things happen here:
     *   1. Project work orders that share a (scheduledDate, projectName)
     *      key are collapsed into a single chip — the spec calls for
     *      bucketing siblings on the same day so a dense construction
     *      schedule reads as one row rather than five.
     *   2. Everything else (standard WOs, inspections, project WOs with
     *      no project_name) renders one chip per event. */
    const rbcEvents: RbcEvent[] = useMemo(
        () => buildRbcEvents(events),
        [events]
    );

    /* Background/border per category. White text + small padding stays
     * the same across categories so the chip footprint doesn't shift. */
    const eventPropGetter = (rbcEvent: RbcEvent) => {
        const palette = CATEGORY_COLORS[rbcEvent.category];
        return {
            className: '',
            style: {
                backgroundColor: palette.bg,
                borderColor: palette.border,
                color: '#ffffff',
                borderRadius: '4px',
                padding: '0 4px',
                fontSize: '12px',
            },
        };
    };

    return (
        <div className={['calendar-view', 'h-[480px] tablet:h-[600px] desktop:h-[720px] bg-white', className ?? ''].join(' ')} data-ref="organism-calendar-view">
            <Calendar
                localizer={localizer}
                events={rbcEvents}
                startAccessor="start"
                endAccessor="end"
                allDayAccessor="allDay"
                titleAccessor="title"
                /* Controlled view + date so the toolbar buttons reliably
                 * propagate clicks. Without these, the toolbar visually
                 * highlights the active view but the calendar body
                 * doesn't re-render in some host setups. */
                view={view}
                onView={setView}
                date={date}
                onNavigate={setDate}
                /* Month / Week / Day / List — "List" reuses
                 * react-big-calendar's built-in agenda view (a flat
                 * date|time|event table of rows) but is relabeled in
                 * the toolbar via the `messages` prop below so users
                 * see "List" rather than the library default "Agenda". */
                views={['month', 'week', 'day', 'agenda']}
                /* Toolbar label override — only the agenda view is
                 * renamed; the others fall through to library defaults. */
                messages={{ agenda: 'List' }}
                popup
                eventPropGetter={eventPropGetter}
                onSelectEvent={(rbcEvent: RbcEvent) => {
                    if (!onSelectEvent) return;
                    if (rbcEvent.groupedEvents) {
                        onSelectEvent({ kind: 'group', events: rbcEvent.groupedEvents });
                    } else if (rbcEvent.event) {
                        onSelectEvent({ kind: 'single', event: rbcEvent.event });
                    }
                }}
                className="h-full"
            />
        </div>
    );
};

/* ============================================
 * GROUPING ADAPTER
 * ============================================
 * Pulled out of the component so it can be unit-tested in isolation
 * and exported for the rare consumer that wants the same logic
 * (currently none — kept private as `buildRbcEvents`).
 *
 * Rule: collapse project work orders into one chip per
 * (scheduledDate, projectName) bucket. A bucket of one stays as a
 * single chip — no "Group (1)" labels. Everything else passes through
 * untouched. */
function buildRbcEvents(events: ScheduledEvent[]): RbcEvent[] {
    /* Bucket project WOs by date + project name. The non-bucketed
     * stream (inspections, standard WOs, project WOs missing a
     * project name) flows straight into the output. */
    const projectBuckets = new Map<string, ScheduledEvent[]>();
    const passthrough: ScheduledEvent[] = [];

    for (const e of events) {
        const category = getEventCategory(e);
        if (category === 'project_work_order' && e.projectName) {
            const key = `${e.scheduledDate}::${e.projectName.trim().toLowerCase()}`;
            const bucket = projectBuckets.get(key);
            if (bucket) {
                bucket.push(e);
            } else {
                projectBuckets.set(key, [e]);
            }
        } else {
            passthrough.push(e);
        }
    }

    const rbc: RbcEvent[] = passthrough.map((e) => ({
        title: e.title,
        /* react-big-calendar requires real Date objects, not ISO strings.
         * Date constructor parses both ISO datetime strings and
         * YYYY-MM-DD date-only strings cleanly. */
        start: new Date(e.start),
        end: new Date(e.end || e.start),
        allDay: e.allDay,
        category: getEventCategory(e),
        count: 1,
        event: e,
    }));

    for (const bucket of Array.from(projectBuckets.values())) {
        if (bucket.length === 1) {
            const e = bucket[0];
            rbc.push({
                title: e.title,
                start: new Date(e.start),
                end: new Date(e.end || e.start),
                allDay: e.allDay,
                category: 'project_work_order',
                count: 1,
                event: e,
            });
            continue;
        }
        /* Multi-event bucket: present as one chip spanning the earliest
         * start to the latest end. Title includes the count so the
         * collapsed nature is obvious at a glance. */
        const sorted = [...bucket].sort(
            (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
        );
        const lead = sorted[0];
        const latestEnd = sorted.reduce((acc, e) => {
            const t = new Date(e.end || e.start).getTime();
            return t > acc ? t : acc;
        }, new Date(lead.end || lead.start).getTime());
        rbc.push({
            title: `${lead.projectName ?? 'Project'} (${sorted.length})`,
            start: new Date(lead.start),
            end: new Date(latestEnd),
            /* If any sibling is timed, treat the chip as timed so it
             * lays out properly in week/day views. */
            allDay: sorted.every((e) => e.allDay),
            category: 'project_work_order',
            count: sorted.length,
            groupedEvents: sorted,
        });
    }

    return rbc;
}

export default CalendarView;
